import React, { memo, ReactNode, useEffect, useState } from 'react'
import { B2COrderResponseFragment } from 'parkdepot-shared/gql/graphql'
import { CARPAY_STATUS } from 'parkdepot-shared/models/database'
import { DateTime } from 'luxon'
import CarpayOrderDiscount from './CarpayOrderDiscount.component'
import { CountdownTextContainer } from './CarpayOrderCard.style'
import { useTranslation } from 'react-i18next'
import IncompleteCarpayOrderCard from './Cards/IncompleteCarpayOrderCard.component'
import UnpaidCarpayOrderCard from './Cards/UnpaidCarpayOrderCard.component'
import PaidCarpayOrderCard from './Cards/PaidCarpayOrderCard.component'
import EnforcedCarpayOrderCard from './Cards/EnforcedCarpayOrderCard.component'

export const Countdown = memo(({ paymentDeadline }: { paymentDeadline?: string | null }) => {
    const { t } = useTranslation()
    const [, forceUpdate] = useState({})

    useEffect(() => {
        const timer = setInterval(() => {
            forceUpdate({})
        }, 1000)

        return () => clearInterval(timer)
    }, [])

    const paymentDeadlineDate = paymentDeadline ? DateTime.fromISO(paymentDeadline) : DateTime.now()
    const timeLeftDifference = paymentDeadlineDate.diff(DateTime.now())
    let timeLeft = timeLeftDifference.shiftTo('hours', 'minutes', 'seconds').toFormat("h'h' m'm' s's'")

    if (timeLeftDifference.toMillis() <= 0) {
        timeLeft = '0h 0m 0s'
    }

    return (
        <CountdownTextContainer>
            <span>{t('carpay.orderCard.countdownTitle')}</span>
            <b>{timeLeft}</b>
        </CountdownTextContainer>
    )
})

Countdown.displayName = 'Countdown'

export interface SpecificCarpayCard {
    order: B2COrderResponseFragment['order']
    lot: B2COrderResponseFragment['lot']
    discount?: ReactNode
    fullyWhitelisted: boolean
    priceToBePaid: number
    isLast: boolean
    disabled?: boolean
    currency: string
    onClick: () => void
    paymentDeadline?: string | null
}

interface CarpayOrderCardProps {
    data: B2COrderResponseFragment
    isLast: boolean
    disabled?: boolean
    onClick: () => void
}

const CarpayOrderCard = ({ data, isLast, disabled, onClick }: CarpayOrderCardProps) => {
    const { order, price, lot } = data

    const fullPrice = price.parking_price
    const whitelistDiscount = Math.max(price.parking_price - price.illegal_parking_price, 0)
    const paymentDeadline = price.payment_deadline
    const voucherDiscount = price.applied_discount
    const priceToBePaid = price.discounted_parking_price
    const currency = price.currency
    const fullyWhitelisted = !price.billable

    let discount
    if (whitelistDiscount || voucherDiscount) {
        discount = (
            <CarpayOrderDiscount
                fullPrice={fullPrice}
                currency={currency}
                voucherDiscount={voucherDiscount}
                whitelistDiscount={whitelistDiscount}
            />
        )
    }

    const cardProps = {
        order,
        lot,
        discount,
        priceToBePaid,
        fullyWhitelisted,
        isLast,
        disabled,
        onClick,
        currency,
        paymentDeadline
    }

    switch (order.status) {
        case CARPAY_STATUS.PAID:
            return <PaidCarpayOrderCard {...cardProps} />
        case CARPAY_STATUS.INCOMPLETE:
            return <IncompleteCarpayOrderCard {...cardProps} />
        case CARPAY_STATUS.UNPAID:
            return <UnpaidCarpayOrderCard {...cardProps} />
        default:
            return <EnforcedCarpayOrderCard {...cardProps} />
    }
}

export default CarpayOrderCard
